import React from 'react'
import { Box, IconButton, Menu, MenuItem, Typography, Avatar, Chip } from '@mui/material'
import { styled, alpha } from '@mui/material/styles'
import { Comment } from '@/types'
import { MoreHoriz as MoreHorizIcon, DeleteOutline, FavoriteOutlined } from '@mui/icons-material'
import { UserAvatar } from './Avatar'
import SVG from 'react-inlinesvg'
import { SVGS } from '@/assets/svgs'
import CustomTimeAgo from './CustomTimeAgo'
import { useLocalStorage } from '@/hooks/common'
import { convertToGMT } from '@/utils/helper'

// Simple Discord-style comment
const ModernCommentContainer = styled(Box)({
  padding: '8px 16px',
  margin: '0',
  backgroundColor: 'transparent',

  '&:hover': {
    backgroundColor: '#f9fafb',
  },
})

const CommentHeader = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
  marginBottom: '8px',
})

const UserInfo = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
  flex: 1,
})

const ModernAvatar = styled(Box)({
  width: 32,
  height: 32,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 50%, #c084fc 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,

  '& p': {
    fontSize: '13px',
    fontWeight: 600,
    color: '#ffffff',
    margin: 0,
  },
})

const UserDetails = styled(Box)({
  flex: 1,
})

const UserName = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#111827',
  lineHeight: 1.3,
  marginBottom: '2px',
})

const TimeStamp = styled(Typography)({
  fontSize: '12px',
  fontWeight: 400,
  color: '#6b7280',
  lineHeight: 1.3,
})

const CommentText = styled(Typography)({
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: 1.4,
  color: '#374151',
  marginLeft: '44px',
  marginTop: '2px',
})

const ModernMoreButton = styled(IconButton)({
  width: '24px',
  height: '24px',
  padding: 0,

  '&:hover': {
    backgroundColor: '#f3f4f6',
  },

  '& svg': {
    fontSize: '16px',
    color: '#9ca3af',
  },
})

const ModernMenu = styled(Menu)({
  '& .MuiPaper-root': {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    padding: '4px',
    minWidth: '120px',
  },

  '& .MuiMenuItem-root': {
    color: '#374151',
    fontSize: '13px',
    fontWeight: 400,
    borderRadius: '4px',
    padding: '8px 12px',

    '&:hover': {
      backgroundColor: '#f9fafb',
    },

    '& .MuiSvgIcon-root': {
      marginRight: '8px',
      fontSize: '16px',
    },
  },
})

const YouChip = styled(Chip)({
  height: '18px',
  fontSize: '11px',
  fontWeight: 500,
  backgroundColor: '#f3f4f6',
  color: '#6b7280',
  borderRadius: '4px',

  '& .MuiChip-label': {
    padding: '0 6px',
  },
})

interface ModernCommentProps {
  comment: Comment
  deletable: boolean
  onClickDelete?: () => void
}

export function ModernComment({ comment, deletable, onClickDelete }: ModernCommentProps) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [ewUser] = useLocalStorage('ewUser', null)
  const open = Boolean(anchorEl)

  const isCurrentUser = ewUser?.userID === comment.senderID

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleDelete = () => {
    handleClose()
    if (onClickDelete) onClickDelete()
  }

  const getDisplayName = () => {
    // Always show first name
    const firstName = comment.senderName.split(' ')[0]
    return firstName
  }

  const getInitials = () => {
    const names = comment.senderName.split(' ')
    if (names.length >= 2) {
      return `${names[0].charAt(0)}${names[1].charAt(0)}`.toUpperCase()
    }
    return comment.senderName.charAt(0).toUpperCase()
  }

  return (
    <ModernCommentContainer>
      <CommentHeader>
        <UserAvatar
          borderRadius={100}
          name={comment.senderName}
          url={comment.profilePicURL}
          size={32}
        />
        {/* <ModernAvatar className='avatar'>
          <Typography component='p'>{getInitials()}</Typography>
        </ModernAvatar> */}

        <UserDetails>
          <Box display='flex' alignItems='center' gap='6px'>
            <UserName>{getDisplayName()}</UserName>
            {isCurrentUser && <YouChip label='You' size='small' />}
            <TimeStamp>
              <CustomTimeAgo date={comment.dateAdded} />
            </TimeStamp>
          </Box>
        </UserDetails>

        {deletable && (
          <>
            <ModernMoreButton
              onClick={handleClick}
              aria-controls={open ? 'comment-menu' : undefined}
              aria-haspopup='true'
              aria-expanded={open ? 'true' : undefined}
            >
              <MoreHorizIcon />
            </ModernMoreButton>

            <ModernMenu
              id='comment-menu'
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem onClick={handleDelete}>
                <DeleteOutline />
                Delete
              </MenuItem>
            </ModernMenu>
          </>
        )}
      </CommentHeader>

      <CommentText>{comment.comment}</CommentText>
    </ModernCommentContainer>
  )
}
